const reportClaimTypesSchema = z
  .array(z.object({ id: z.string(), label: z.string() }))
  .optional();
import { MATTER_STATUS_CATEGORY_OPTIONS } from "@/lib/data/static/matterStatics";
import { z } from "zod";

const reportMatterStatuteOfLimitationsArrayItemSchema = z
  .object({
    matterStatuteOfLimitationsConfirmed: z.boolean().optional(),
    matterStatuteOfLimitationsDate: z.string().optional(),
    matterStatuteOfLimitationsDescription: z.string().optional(),
    matterStatuteOfLimitationsId: z.string().optional(),
  })
  .passthrough();

const reportMatterStatuteOfLimitationsSchema = z
  .union([z.string(), z.array(reportMatterStatuteOfLimitationsArrayItemSchema)])
  .nullable()
  .optional();

const reportMatterInsuranceCoverageSchema = z
  .object({
    matterContactConnectionContactName: z.string().nullable().optional(),
    coverage: z.string().nullable().optional(),
    matterInsuranceId: z.string().optional(),
  })
  .passthrough();

const reportMatterInsuranceLiabilitySchema = z
  .object({
    matterContactConnectionContactName: z.string().nullable().optional(),
    matterInsuranceId: z.string().optional(),
    liability: z.string().nullable().optional(),
    liabilityPercentage: z.coerce.number().nullable().optional(),
  })
  .passthrough();

export const ReportsMatterSearchReportEmailSchema = z.object({
  address: z.string().optional(),
  id: z.string().optional(),
  isPrimary: z.boolean().optional(),
  label: z.string().optional(),
  description: z.string().optional(),
});

export const ReportsMatterSearchReportPhoneSchema = z.object({
  label: z.string().optional(),
  id: z.string().optional(),
  extension: z.string().optional(),
  number: z.string().optional(),
  description: z.string().optional(),
  isPrimary: z.boolean().optional(),
});

export const ReportsMatterActivitySchema = z
  .object({
    claimTypes: reportClaimTypesSchema,
    dateCreated: z.string().optional(),
    dateModified: z.string().optional(),
    matterActivityReportAggregateActionsCompleted: z
      .array(z.record(z.number()))
      .optional(),
    matterActivityReportAggregateActivityTotalHours: z.number().optional(),
    matterActivityReportAggregateDate: z.string().optional(),
    matterActivityReportAggregateFirmId: z.string().optional(),
    matterActivityReportAggregateId: z.string().optional(),
    matterActivityReportAggregateMatterId: z.string().optional(),
    matterActivityReportAggregateMatterStatus: z.string().optional(),
    matterActivityReportAggregateMatterStatusCategory: z.string().optional(),
    matterActivityReportAggregateMatterTeam: z.array(z.string()).optional(),
    matterActivityReportAggregateMatterTitle: z.string().optional(),
    matterActivityReportAggregateIncidentType: z.string().nullish(),
    matterActivityReportAggregateTimeline: z
      .array(
        z.object({
          matterActivityReportDateTimeEnd: z.string().optional(),
          matterActivityReportDateTimeStart: z.string().optional(),
        }),
      )
      .optional(),
    pk: z.string().optional(),
    reportIndexPk: z.string().optional(),
    reportIndexSk: z.string().optional(),
    sk: z.string().optional(),
  })
  .passthrough();

export const ReportsMatterStatusCountSchema = z.object({
  category: z.string().optional(),
  label: z.string(),
  count: z.number(),
});

export const ReportsRoleAssignmentStatusSchema = z.object({
  id: z.string(),
  label: z.string(),
  category: z.string(),
  categoryLabel: z.string(),
});

export const ReportsRoleAssignmentRowSchema = z.object({
  userId: z.string(),
  userName: z.string().nullable(),
  totalMatters: z.number(),
  assignedLast24Hours: z.number(),
  assignedLast7Days: z.number(),
  assignedLast30Days: z.number(),
  statusCounts: z.record(z.string(), z.number()),
});

export const ReportsRoleAssignmentsSchema = z.object({
  role: z.object({ id: z.string(), label: z.string() }),
  statuses: z.array(ReportsRoleAssignmentStatusSchema),
  metrics: z.object({
    totalUsers: z.number(),
    totalAssignments: z.number(),
  }),
  rows: z.array(ReportsRoleAssignmentRowSchema),
});

export const ReportsMatterSettlementMetricsSchema = z.object({
  sumOfDemands: z.number().optional(),
  sumOfDemandsFees: z.number().optional(),
  sumOfOffers: z.number().optional(),
  sumOfOffersFees: z.number().optional(),
  sumOfOffersWithIssues: z.number().optional(),
  sumOfOffersWithIssuesFees: z.number().optional(),
  sumOfPendingTrustChecks: z.number().optional(),
  sumOfPendingTrustChecksFees: z.number().optional(),
  sumOfTrustChecksIn: z.number().optional(),
  sumOfTrustChecksInFees: z.number().optional(),
});

export const ReportsMatterSettlementDemandSchema = z
  .object({
    claimTypes: reportClaimTypesSchema,
    dateCreated: z.string().optional(),
    dateModified: z.string().optional(),
    matterSettlementReportDemandAmount: z.number().optional(),
    matterSettlementReportDemandAttorneyFeeAmount: z.number().optional(),
    matterSettlementReportDemandDateExpires: z.string().optional(),
    matterSettlementReportDemandDateSent: z.string().optional(),
    matterSettlementReportDemandId: z.string().optional(),
    matterSettlementReportDemandPolicyLimit: z.boolean().optional(),
    matterSettlementReportFirmId: z.string().optional(),
    matterSettlementReportId: z.string().optional(),
    matterSettlementReportInsuranceId: z.string().optional(),
    matterSettlementReportInsuranceLimitPerIncident: z.number().optional(),
    matterSettlementReportInsuranceLimitPerPerson: z.number().optional(),
    matterSettlementReportInsuranceType: z.string().optional(),
    matterSettlementReportMatterAssigneeUserIds: z.array(z.string()).optional(),
    matterSettlementReportMatterId: z.string().optional(),
    matterSettlementReportMatterStatusCategory: z.string().optional(),
    matterSettlementReportMatterStatusLabel: z.string().optional(),
    matterSettlementReportMatterTeams: z.array(z.string()).optional(),
    matterSettlementReportMatterTitle: z.string().optional(),
    matterSettlementReportIncidentType: z.string().nullish(),
    matterSettlementReportMedicalBillTotal: z.number().optional(),
    pk: z.string().optional(),
    reportIndexPk: z.string().optional(),
    reportIndexSk: z.string().optional(),
    sk: z.string().optional(),
  })
  .passthrough();

export const ReportsMatterSettlementOfferSchema = z
  .object({
    claimTypes: reportClaimTypesSchema,
    dateCreated: z.string().optional(),
    dateModified: z.string().optional(),
    matterSettlementReportFirmId: z.string().optional(),
    matterSettlementReportId: z.string().optional(),
    matterSettlementReportInsuranceId: z.string().optional(),
    matterSettlementReportInsuranceLimitPerIncident: z.number().optional(),
    matterSettlementReportInsuranceLimitPerPerson: z.number().optional(),
    matterSettlementReportInsuranceType: z.string().optional(),
    matterSettlementReportMatterAssigneeUserIds: z.array(z.string()).optional(),
    matterSettlementReportMatterId: z.string().optional(),
    matterSettlementReportMatterStatusCategory: z.string().optional(),
    matterSettlementReportMatterStatusLabel: z.string().optional(),
    matterSettlementReportMatterTeams: z.array(z.string()).optional(),
    matterSettlementReportMatterTitle: z.string().optional(),
    matterSettlementReportIncidentType: z.string().nullish(),
    matterSettlementReportMedicalBillTotal: z.number().optional(),
    matterSettlementReportOfferAccepted: z.boolean().optional(),
    matterSettlementReportOfferAcceptedDate: z.string().optional(),
    matterSettlementReportOfferAmount: z.number().optional(),
    matterSettlementReportOfferAttorneyFeeAmount: z.number().optional(),
    matterSettlementReportOfferDateReceived: z.string().optional(),
    matterSettlementReportOfferId: z.string().optional(),
    matterSettlementReportOfferPolicyLimit: z.boolean().optional(),
    pk: z.string().optional(),
    reportIndexPk: z.string().optional(),
    reportIndexSk: z.string().optional(),
    sk: z.string().optional(),
  })
  .passthrough();

export const ReportsMatterSettlementCounterSchema = z
  .object({
    claimTypes: reportClaimTypesSchema,
    dateCreated: z.string().optional(),
    dateModified: z.string().optional(),
    matterSettlementReportCounterAccepted: z.boolean().optional(),
    matterSettlementReportCounterAcceptedDate: z.string().optional(),
    matterSettlementReportCounterAmount: z.number().optional(),
    matterSettlementReportCounterAttorneyFeeAmount: z.number().optional(),
    matterSettlementReportCounterDateSent: z.string().optional(),
    matterSettlementReportCounterId: z.string().optional(),
    matterSettlementReportCounterPolicyLimit: z.boolean().optional(),
    matterSettlementReportCounterStatus: z.string().optional(),
    matterSettlementReportFirmId: z.string().optional(),
    matterSettlementReportId: z.string().optional(),
    matterSettlementReportInsuranceId: z.string().optional(),
    matterSettlementReportInsuranceLimitPerIncident: z.number().optional(),
    matterSettlementReportInsuranceLimitPerPerson: z.number().optional(),
    matterSettlementReportInsuranceType: z.string().optional(),
    matterSettlementReportMatterAssigneeUserIds: z.array(z.string()).optional(),
    matterSettlementReportMatterId: z.string().optional(),
    matterSettlementReportMatterStatusCategory: z.string().optional(),
    matterSettlementReportMatterStatusLabel: z.string().optional(),
    matterSettlementReportMatterTeams: z.array(z.string()).optional(),
    matterSettlementReportMatterTitle: z.string().optional(),
    matterSettlementReportIncidentType: z.string().nullish(),
    matterSettlementReportMedicalBillTotal: z.number().optional(),
    pk: z.string().optional(),
    reportIndexPk: z.string().optional(),
    reportIndexSk: z.string().optional(),
    sk: z.string().optional(),
  })
  .passthrough();

export const ReportsMatterSettlementTrustCheckSchema = z.record(z.unknown());

export const ReportsMatterSettlementReportSchema = z.object({
  metrics: ReportsMatterSettlementMetricsSchema.optional(),
  matterSettlementCounters: z
    .array(ReportsMatterSettlementCounterSchema)
    .optional(),
  matterSettlementDemands: z
    .array(ReportsMatterSettlementDemandSchema)
    .optional(),
  matterSettlementOffers: z
    .array(ReportsMatterSettlementOfferSchema)
    .optional(),
  matterTrustChecks: z
    .array(ReportsMatterSettlementTrustCheckSchema)
    .optional(),
});

export const ReportsMatterDemandReportMetricsSchema = z.object({
  MedpayDemandAmount: z.coerce.number().optional(),
  UmDemandAmount: z.coerce.number().optional(),
  UimDemandAmount: z.coerce.number().optional(),
  firstPartyDemandAmount: z.coerce.number().optional(),
  thirdPartyDemandAmount: z.coerce.number().optional(),
  totalDemandAmount: z.coerce.number().optional(),
  totalDemandSent: z.coerce.number().optional(),
});

export const ReportsMatterDemandReportSchema = z.object({
  claimTypes: reportClaimTypesSchema,
  amount: z.coerce.number().nullable().optional(),
  assigneeUserIds: z.array(z.string()).optional(),
  attorneyFeeAmount: z.coerce.number().nullable().optional(),
  attorneyFeePercentage: z.coerce.number().nullable().optional(),
  coverageType: z.string().nullable().optional(),
  dateExpires: z.string().nullable().optional(),
  dateSent: z.string().nullable().optional(),
  firstParty: z.boolean().optional(),
  id: z.string(),
  insuranceId: z.string().nullable().optional(),
  insuranceType: z.string().nullable().optional(),
  matterId: z.string(),
  matterTitle: z.string().nullable().optional(),
  incidentTypeId: z.string().nullable().optional(),
  incidentTypeLabel: z.string().nullable().optional(),
  originalMedicalBillTotal: z.coerce.number().nullable().optional(),
  policyLimit: z.boolean().optional(),
  policyLimitPerIncident: z.coerce.number().nullable().optional(),
  policyLimitPerPerson: z.coerce.number().nullable().optional(),
  teams: z
    .array(
      z.object({
        id: z.string(),
        label: z.string(),
      }),
    )
    .optional(),
  thirdParty: z.boolean().optional(),
});

export const ReportsMatterDemandReportResponseSchema = z.object({
  metrics: ReportsMatterDemandReportMetricsSchema.optional(),
  rows: z.array(ReportsMatterDemandReportSchema).optional(),
});

export const ReportsMatterRecordRequestsReportSchema = z
  .object({
    claimTypes: reportClaimTypesSchema,
    dateCreated: z.string().optional(),
    dateModified: z.string().optional(),
    matterRecordRequestsReportFirmId: z.string().optional(),
    matterRecordRequestsReportId: z.string().optional(),
    matterRecordRequestsReportIncidentReportStatus: z.string().optional(),
    matterRecordRequestsReportMatterAssigneeUserIds: z
      .array(z.string())
      .optional(),
    matterRecordRequestsReportMatterId: z.string().optional(),
    matterRecordRequestsReportMatterStatusLabel: z.string().optional(),
    matterRecordRequestsReportMatterTeams: z.array(z.string()).optional(),
    matterRecordRequestsReportMatterTitle: z.string().optional(),
    matterRecordRequestsReportIncidentType: z.string().nullish(),
    matterRecordRequestsReportRequestKind: z.string().optional(),
    matterRecordRequestsReportRequestLabel: z.string().optional(),
    matterRecordRequestsReportRequestDate: z.string().optional(),
    matterRecordRequestsReportReceivedDate: z.string().optional(),
    matterRecordRequestsReportRejectedDate: z.string().optional(),
    matterRecordRequestsReportRequestStatus: z.string().optional(),
    matterRecordRequestsReportMedicalTreatmentRecordStatus: z
      .string()
      .optional(),
    pk: z.string().optional(),
    reportIndexPk: z.string().optional(),
    reportIndexSk: z.string().optional(),
    sk: z.string().optional(),
  })
  .passthrough();

export const ReportsOpenMattersReportDefendantPolicyLimitSchema = z.object({
  openMattersReportMatterDefendantPolicyLimitConfirmed: z.boolean().optional(),
  openMattersReportMatterDefendantPolicyLimitPerPerson: z
    .union([z.number(), z.string()])
    .optional(),
  openMattersReportMatterDefendantPolicyLimitPerIncident: z
    .union([z.number(), z.string()])
    .optional(),
});

export const ReportsOpenMattersReportSchema = z
  .object({
    claimTypes: reportClaimTypesSchema,
    dateCreated: z.string().optional(),
    dateModified: z.string().optional(),
    openMattersReportFirmId: z.string().optional(),
    openMattersReportId: z.string().optional(),
    openMattersReportMatterAssignee: z.array(z.string()).optional(),
    openMattersReportMatterDateOfLoss: z.string().optional(),
    openMattersReportMatterDefendantPolicyLimit: z
      .array(ReportsOpenMattersReportDefendantPolicyLimitSchema)
      .optional(),
    openMattersReportMatterExpenseTotal: z.number().optional(),
    openMattersReportMatterId: z.string().optional(),
    openMattersReportMatterIncidentState: z.string().optional(),
    openMattersReportMedicalTreatmentBillTotal: z.number().optional(),
    openMattersReportMatterStatusCategory: z.string().optional(),
    openMattersReportMatterStatusLabel: z.string().optional(),
    openMattersReportMatterStatusTransitionDate: z.string().optional(),
    openMattersReportMatterStatuteOfLimitations:
      reportMatterStatuteOfLimitationsSchema,
    openMattersReportMatterStatuteOfLimitationsConfirmed: z
      .boolean()
      .optional(),
    openMattersReportMatterTag: z.array(z.string()).optional(),
    openMattersReportMatterTeam: z.array(z.string()).optional(),
    openMattersReportMatterTitle: z.string().optional(),
    openMattersReportIncidentTypeClaimType: z.string().nullish(),
    openMattersReportIncidentTypeLabel: z.string().nullish(),
    openMattersReportDaysInMatterStatus: z.number().optional(),
    pk: z.string().optional(),
    reportIndexPk: z.string().optional(),
    reportIndexSk: z.string().optional(),
    sk: z.string().optional(),
  })
  .passthrough();

export const ReportsUserProductivityReportActionCompletedSchema = z.record(
  z.number(),
);

export const ReportsUserProductivityReportTimelineSchema = z.object({
  userProductivityReportDateTimeEnd: z.string().optional(),
  userProductivityReportDateTimeStart: z.string().optional(),
});

export const ReportsUserProductivityReportAggregateSchema = z
  .object({
    dateCreated: z.string().optional(),
    dateModified: z.string().optional(),
    pk: z.string().optional(),
    reportIndexPk: z.string().optional(),
    reportIndexSk: z.string().optional(),
    sk: z.string().optional(),
    userProductivityReportAggregateActionsCompleted: z
      .array(ReportsUserProductivityReportActionCompletedSchema)
      .optional(),
    userProductivityReportAggregateActivityTotalHours: z.number().optional(),
    userProductivityReportAggregateDate: z.string().optional(),
    userProductivityReportAggregateFirmId: z.string().optional(),
    userProductivityReportAggregateId: z.string().optional(),
    userProductivityReportAggregateMatterTeam: z.array(z.string()).optional(),
    userProductivityReportAggregateTimeline: z
      .array(ReportsUserProductivityReportTimelineSchema)
      .optional(),
    userProductivityReportAggregateUserId: z.string().optional(),
  })
  .passthrough();

export const ReportsMatterSearchReportSchema = z.object({
  claimTypes: reportClaimTypesSchema,
  dateCreated: z.string(),
  dateModified: z.string(),
  matterSearchReportFirmId: z.string(),
  matterSearchReportId: z.string(),
  matterSearchReportMatterAssignee: z.array(z.string()).nullable().optional(),
  matterSearchReportMatterClientEmail: z
    .array(z.union([ReportsMatterSearchReportEmailSchema, z.string()]))
    .nullable()
    .optional(),
  matterSearchReportMatterClientName: z.string().nullable().optional(),
  matterSearchReportMatterClientPhone: z
    .array(z.union([ReportsMatterSearchReportPhoneSchema, z.string()]))
    .nullable()
    .optional(),
  matterSearchReportMatterDateOfLoss: z.string().nullable().optional(),
  matterSearchReportMatterDefendantName: z
    .array(z.string())
    .nullable()
    .optional(),
  matterSearchReportMatterEmailAddress: z.string().nullable().optional(),
  matterSearchReportMatterExpenseTotal: z.number().optional(),
  matterSearchReportMatterId: z.string().nullable().optional(),
  matterSearchReportMatterIncidentAddressCity: z.string().nullable().optional(),
  matterSearchReportMatterIncidentAddressState: z
    .string()
    .nullable()
    .optional(),
  matterSearchReportclaimNumber: z.array(z.string()).nullable().optional(),
  matterSearchReportMatterInsuranceCoverage: z
    .array(reportMatterInsuranceCoverageSchema)
    .nullable()
    .optional(),
  matterSearchReportMatterInsuranceLiability: z
    .array(reportMatterInsuranceLiabilitySchema)
    .nullable()
    .optional(),
  matterSearchReportpolicyNumber: z.array(z.string()).nullable().optional(),
  matterSearchReportMedicalTreatmentBillTotal: z.number().optional(),
  matterSearchReportMatterStatusCategory: z
    .enum(MATTER_STATUS_CATEGORY_OPTIONS)
    .nullable()
    .optional(),
  matterSearchReportMatterStatusLabel: z.string().nullable().optional(),
  matterSearchReportMatterStatuteOfLimitations:
    reportMatterStatuteOfLimitationsSchema,
  matterSearchReportMatterStatuteOfLimitationsConfirmed: z
    .boolean()
    .nullable()
    .optional(),
  matterSearchReportMatterTag: z.array(z.string()).nullable().optional(),
  matterSearchReportMatterTeam: z.array(z.string()).nullable().optional(),
  matterSearchReportMatterTitle: z.string().nullable().optional(),
  matterSearchReportIncidentType: z.string().nullable().optional(),
  pk: z.string(),
  reportIndexPk: z.string(),
  reportIndexSk: z.string(),
  sk: z.string(),
});

const signatureRequestReportMatterStatuteItemSchema = z
  .object({
    matterStatuteOfLimitationsConfirmed: z.boolean().optional(),
    matterStatuteOfLimitationsDate: z.string().optional(),
  })
  .passthrough();

const signatureRequestReportMatterStatuteOfLimitationsSchema = z
  .union([z.string(), z.array(signatureRequestReportMatterStatuteItemSchema)])
  .nullable()
  .optional();

export const ReportsSignatureRequestReportContactFaxSchema = z
  .object({
    id: z.string().optional(),
    isPrimary: z.boolean().optional(),
    number: z.string().optional(),
  })
  .passthrough();

export const ReportsSignatureRequestReportContactConnectionSchema = z
  .object({
    signatureRequestReportContactConnectionContactEmail: z
      .array(ReportsMatterSearchReportEmailSchema)
      .optional(),
    signatureRequestReportContactConnectionContactFax: z
      .array(ReportsSignatureRequestReportContactFaxSchema)
      .optional(),
    signatureRequestReportContactConnectionContactId: z.string().optional(),
    signatureRequestReportContactConnectionContactIndividualSubTypes: z
      .array(z.string())
      .optional(),
    signatureRequestReportContactConnectionContactName: z.string().optional(),
    signatureRequestReportContactConnectionContactOrganizationSubTypes: z
      .array(z.string())
      .optional(),
    signatureRequestReportContactConnectionContactPhone: z
      .array(ReportsMatterSearchReportPhoneSchema)
      .optional(),
    signatureRequestReportContactConnectionContactType: z.string().optional(),
    signatureRequestReportContactConnectionId: z.string().optional(),
    signatureRequestReportContactConnectionLabel: z.string().optional(),
    signatureRequestReportContactConnectionSignatureRequestComplete: z
      .boolean()
      .optional(),
    signatureRequestReportContactConnectionSignatureRequestPasscode: z
      .string()
      .optional(),
    signatureRequestReportSignatureRequestSignerOrder: z.number().optional(),
  })
  .passthrough();

export const ReportsSignatureRequestReportUserConnectionSchema = z
  .object({
    signatureRequestReportUserConnectionId: z.string().optional(),
    signatureRequestReportUserConnectionLabel: z.string().optional(),
    signatureRequestReportUserConnectionSignatureRequestComplete: z
      .boolean()
      .optional(),
    signatureRequestReportUserConnectionSignatureRequestPasscode: z
      .string()
      .optional(),
    signatureRequestReportSignatureRequestSignerOrder: z.number().optional(),
    signatureRequestReportUserConnectionUserId: z.string().optional(),
    signatureRequestReportUserConnectionUserName: z.string().optional(),
    signatureRequestReportUserConnectionUserEmail: z.string().optional(),
    signatureRequestReportUserConnectionUserPhone: z.string().optional(),
  })
  .passthrough();

export const ReportsSignatureRequestReportSchema = z
  .object({
    dateCreated: z.string().optional(),
    dateModified: z.string().optional(),
    pk: z.string().optional(),
    reportIndexPk: z.string().optional(),
    reportIndexSk: z.string().optional(),
    sk: z.string().optional(),
    signatureRequestReportContactConnections: z
      .array(ReportsSignatureRequestReportContactConnectionSchema)
      .optional(),
    signatureRequestReportFirmId: z.string().optional(),
    signatureRequestReportFirmFileId: z.string().optional(),
    signatureRequestReportMatterFileId: z.string().optional(),
    signatureRequestReportMatterFileName: z.string().optional(),
    signatureRequestReportFirmFileName: z.string().optional(),
    signatureRequestReportFirmFilePresignedUrlKey: z.string().optional(),
    signatureRequestReportId: z.string().optional(),
    signatureRequestReportMatterId: z.string().optional(),
    signatureRequestReportMatterStatusCategory: z.string().optional(),
    signatureRequestReportMatterStatusLabel: z.string().optional(),
    signatureRequestReportMatterStatuteOfLimitations:
      signatureRequestReportMatterStatuteOfLimitationsSchema,
    signatureRequestReportMatterTitle: z.string().optional(),
    signatureRequestReportIncidentType: z.string().nullish(),
    signatureRequestReportSignatureRequestExpirationDate: z.string().optional(),
    signatureRequestReportSignatureRequestFileIdDestination: z
      .string()
      .optional(),
    signatureRequestReportSignatureRequestId: z.string().optional(),
    signatureRequestReportUserConnections: z
      .array(ReportsSignatureRequestReportUserConnectionSchema)
      .optional(),
  })
  .passthrough();
